import type {
  PlannerTask,
  TaskAuditAction,
  TaskAuditActionType,
  TaskAuditEvent,
  TaskAuditSnapshot,
} from './types'

function snapshot(task: PlannerTask): TaskAuditSnapshot {
  return {
    title: task.title,
    notes: task.notes,
    dueAt: task.dueAt,
    priority: task.priority,
    completed: task.completed,
    reminderMinutes: task.reminderMinutes,
  }
}

function action(
  type: TaskAuditActionType,
  before: string | null = null,
  after: string | null = null,
): TaskAuditAction {
  return {
    type,
    before: before?.slice(0, 1000) ?? null,
    after: after?.slice(0, 1000) ?? null,
  }
}

function stableHash(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function eventId(
  taskId: string,
  previousRevision: string,
  before: TaskAuditSnapshot | null,
  after: TaskAuditSnapshot | null,
  actions: TaskAuditAction[],
): string {
  return `${taskId}-${stableHash(
    JSON.stringify({ previousRevision, before, after, actions }),
  )}`.slice(0, 180)
}

export function buildTaskAuditEvent(
  previous: PlannerTask | null,
  next: PlannerTask,
  occurredAt: string,
): TaskAuditEvent | null {
  const actions: TaskAuditAction[] = []

  if (!previous) {
    actions.push(action('created', null, next.title))
  } else {
    if (previous.dueAt !== next.dueAt) {
      actions.push(action('rescheduled', previous.dueAt, next.dueAt))
    }
    if (previous.title !== next.title) {
      actions.push(action('renamed', previous.title, next.title))
    }
    if (previous.notes !== next.notes) {
      actions.push(action('notes_changed'))
    }
    if (previous.priority !== next.priority) {
      actions.push(action('priority_changed', previous.priority, next.priority))
    }
    if (previous.reminderMinutes !== next.reminderMinutes) {
      actions.push(
        action(
          'reminder_changed',
          previous.reminderMinutes === null ? null : String(previous.reminderMinutes),
          next.reminderMinutes === null ? null : String(next.reminderMinutes),
        ),
      )
    }
    if (previous.completed !== next.completed) {
      actions.push(action(next.completed ? 'completed' : 'reopened'))
    }
  }

  if (!actions.length) return null

  const before = previous ? snapshot(previous) : null
  const after = snapshot(next)

  return {
    id: eventId(next.id, previous?.updatedAt ?? 'created', before, after, actions),
    taskId: next.id,
    occurredAt,
    clientOccurredAt: occurredAt,
    dueAt: next.dueAt,
    taskTitle: next.title,
    before,
    after,
    actions,
    source: 'user',
  }
}

export function buildTaskDeletionEvent(
  task: PlannerTask,
  occurredAt: string,
): TaskAuditEvent {
  const before = snapshot(task)
  const actions = [action('deleted', task.title, null)]

  return {
    id: eventId(task.id, task.updatedAt, before, null, actions),
    taskId: task.id,
    occurredAt,
    clientOccurredAt: occurredAt,
    dueAt: task.dueAt,
    taskTitle: task.title,
    before,
    after: null,
    actions,
    source: 'user',
  }
}

export function taskAuditOperationDecision(
  existing: TaskAuditEvent | null,
  requested: TaskAuditEvent,
): 'apply' | 'noop' {
  if (!existing) return 'apply'

  const {
    occurredAt: _existingServerTime,
    clientOccurredAt: _existingClientTime,
    ...existingPayload
  } = existing
  const {
    occurredAt: _requestedServerTime,
    clientOccurredAt: _requestedClientTime,
    ...requestedPayload
  } = requested

  if (JSON.stringify(existingPayload) !== JSON.stringify(requestedPayload)) {
    throw new Error('task-audit-operation-conflict')
  }

  return 'noop'
}

export function taskAuditLabel(type: TaskAuditActionType): string {
  switch (type) {
    case 'created':
      return 'Задача создана'
    case 'rescheduled':
      return 'Задача перенесена'
    case 'renamed':
      return 'Название изменено'
    case 'notes_changed':
      return 'Комментарий изменён'
    case 'priority_changed':
      return 'Приоритет изменён'
    case 'reminder_changed':
      return 'Напоминание изменено'
    case 'completed':
      return 'Задача выполнена'
    case 'reopened':
      return 'Задача возобновлена'
    case 'deleted':
      return 'Задача удалена'
  }
}
